import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, fileUrl, brl } from '../lib/api';
import { beep, initAudio } from '../lib/sound';
import { toast } from 'sonner';
import { Search, X, Plus, Minus, Trash2, CreditCard, Banknote, QrCode, Wallet, ShoppingCart, CheckCircle2 } from 'lucide-react';

const METHODS = [
  { id: 'cash', label: 'Dinheiro', icon: Banknote },
  { id: 'pix', label: 'Pix', icon: QrCode },
  { id: 'debit', label: 'Débito', icon: Wallet },
  { id: 'credit', label: 'Crédito', icon: CreditCard },
];

export default function Vender() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState('');
  const [cart, setCart] = useState({}); // { product_id: {product, qty} }
  const [showCart, setShowCart] = useState(false);
  const [pay, setPay] = useState(false);
  const [method, setMethod] = useState('cash');
  const [amountReceived, setAmountReceived] = useState('');
  const [discount, setDiscount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [flash, setFlash] = useState({}); // {product_id: counter}

  const load = useCallback(async () => {
    const r = await api.get('/products');
    setProducts(r.data);
  }, []);
  useEffect(() => { load(); initAudio(); }, [load]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return products.filter((p) => !p.deleted && (!t || p.name.toLowerCase().includes(t) || (p.barcode || '').includes(t)));
  }, [products, q]);

  const totalItems = Object.values(cart).reduce((s, c) => s + c.qty, 0);
  const subtotal = Object.values(cart).reduce((s, c) => s + c.qty * c.product.sale_price, 0);
  const disc = Math.min(subtotal, Number(discount) || 0);
  const total = subtotal - disc;

  const addToCart = (p) => {
    if (!p.active || p.stock <= 0) { toast.error('Sem estoque'); return; }
    const cur = cart[p.id]?.qty || 0;
    if (cur >= p.stock) { toast.error(`Estoque máximo: ${p.stock}`); return; }
    setCart({ ...cart, [p.id]: { product: p, qty: cur + 1 } });
    beep();
    setFlash((f) => ({ ...f, [p.id]: (f[p.id] || 0) + 1 }));
    setTimeout(() => setFlash((f) => { const n = { ...f }; n[p.id] = Math.max(0, (n[p.id] || 1) - 1); if (n[p.id] === 0) delete n[p.id]; return n; }), 400);
  };

  const changeQty = (pid, delta) => {
    const c = cart[pid]; if (!c) return;
    const nqty = c.qty + delta;
    if (nqty <= 0) { const nc = { ...cart }; delete nc[pid]; setCart(nc); return; }
    if (nqty > c.product.stock) { toast.error('Estoque insuficiente'); return; }
    setCart({ ...cart, [pid]: { ...c, qty: nqty } });
  };

  const removeItem = (pid) => { const n = { ...cart }; delete n[pid]; setCart(n); };

  const finalize = async () => {
    if (totalItems === 0) return;
    setProcessing(true);
    try {
      const items = Object.values(cart).map((c) => ({ product_id: c.product.id, quantity: c.qty }));
      const amt = method === 'cash' ? Number(amountReceived) || total : total;
      if (method === 'cash' && amt < total) { toast.error('Valor recebido insuficiente'); setProcessing(false); return; }
      const r = await api.post('/sales', { items, payment_method: method, discount: disc, amount_received: amt });
      toast.success(`Venda finalizada: ${brl(r.data.total)}${r.data.change > 0 ? ' · Troco: ' + brl(r.data.change) : ''}`);
      setCart({}); setPay(false); setShowCart(false); setAmountReceived(''); setDiscount('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao finalizar');
    } finally { setProcessing(false); }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="sticky top-0 bg-white z-10 px-3 py-2 border-b border-slate-200">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou código..."
                 data-testid="pos-search" className="w-full h-11 pl-10 pr-4 rounded-xl bg-slate-100 border border-transparent focus:bg-white focus:border-orange-500 focus:outline-none text-sm" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 pb-56 thin-scroll">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {filtered.map((p) => {
            const disabled = !p.active || p.stock <= 0;
            const qty = cart[p.id]?.qty || 0;
            return (
              <button
                key={p.id}
                data-testid={`pos-product-${p.id}`}
                onClick={() => !disabled && addToCart(p)}
                className={`relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden text-left no-tap-highlight
                  ${disabled ? 'opacity-50 grayscale pointer-events-none' : 'active:scale-[0.96] transition-transform'} ${flash[p.id] ? 'tap-ring' : ''}`}
              >
                <div className="aspect-square bg-slate-100 overflow-hidden">
                  {p.photo_path ? (
                    <img src={fileUrl(p.photo_path)} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-slate-400 text-3xl font-black">{p.name.slice(0, 1)}</div>
                  )}
                </div>
                <div className="p-2">
                  <div className="text-[13px] font-bold leading-tight line-clamp-2 min-h-[32px]">{p.name}</div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="text-orange-600 font-black text-sm">{brl(p.sale_price)}</div>
                    <div className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${p.stock <= (p.min_stock || 0) ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-600'}`}>
                      {p.stock <= 0 ? 'Sem est.' : `${p.stock} un`}
                    </div>
                  </div>
                </div>
                {qty > 0 && (
                  <div key={qty} data-testid={`pos-qty-${p.id}`} className="absolute top-2 right-2 min-w-[28px] h-7 px-2 rounded-full bg-orange-500 text-white font-black text-sm grid place-items-center shadow-lg animate-pop-badge">
                    ×{qty}
                  </div>
                )}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-14">
              <div className="text-slate-500 mb-3">{products.length === 0 ? 'Nenhum produto cadastrado' : 'Nenhum produto encontrado'}</div>
              {products.length === 0 && (
                <a href="/products" data-testid="pos-goto-products"
                   className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-orange-500 text-white font-bold active:scale-[0.98]">
                  + Adicionar produto
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom cart bar - sits ABOVE the bottom nav */}
      <div className="fixed bottom-[64px] left-0 right-0 bg-white border-t border-slate-200 p-3 z-20 pb-[calc(env(safe-area-inset-bottom)+8px)]">
        <div className="flex gap-2 items-center max-w-xl mx-auto">
          <button onClick={() => setShowCart(true)} data-testid="pos-open-cart"
                  className="flex-1 h-12 rounded-2xl bg-slate-100 flex items-center justify-between px-4 no-tap-highlight active:scale-[0.98] transition-transform">
            <div className="flex items-center gap-2">
              <ShoppingCart size={18} /><span className="font-bold text-sm">{totalItems} {totalItems === 1 ? 'item' : 'itens'}</span>
            </div>
            <div className="font-black">{brl(subtotal)}</div>
          </button>
          <button disabled={totalItems === 0} onClick={() => setPay(true)} data-testid="pos-checkout"
                  className="h-12 px-5 rounded-2xl bg-orange-500 text-white font-black disabled:opacity-50 active:scale-[0.98] transition-transform no-tap-highlight">
            Cobrar
          </button>
        </div>
      </div>

      {/* Cart sheet */}
      {showCart && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end" onClick={() => setShowCart(false)} data-testid="cart-sheet">
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full rounded-t-3xl max-h-[85vh] flex flex-col">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="font-black text-lg">Carrinho</div>
              <button onClick={() => setShowCart(false)} data-testid="cart-close"><X /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {Object.values(cart).map((c) => (
                <div key={c.product.id} className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl bg-slate-100 overflow-hidden shrink-0">
                    {c.product.photo_path && <img src={fileUrl(c.product.photo_path)} className="w-full h-full object-cover" alt="" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{c.product.name}</div>
                    <div className="text-xs text-slate-500">{brl(c.product.sale_price)} · {brl(c.product.sale_price * c.qty)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => changeQty(c.product.id, -1)} data-testid={`cart-dec-${c.product.id}`} className="w-8 h-8 rounded-lg bg-slate-100 grid place-items-center active:scale-95"><Minus size={16} /></button>
                    <div className="w-8 text-center font-black">{c.qty}</div>
                    <button onClick={() => changeQty(c.product.id, +1)} data-testid={`cart-inc-${c.product.id}`} className="w-8 h-8 rounded-lg bg-slate-100 grid place-items-center active:scale-95"><Plus size={16} /></button>
                    <button onClick={() => removeItem(c.product.id)} data-testid={`cart-remove-${c.product.id}`} className="w-8 h-8 rounded-lg bg-red-50 text-red-600 grid place-items-center active:scale-95 ml-1"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
              {totalItems === 0 && <div className="text-center text-slate-500 py-10">Carrinho vazio</div>}
            </div>
            <div className="p-4 border-t border-slate-100 space-y-2">
              <div className="flex items-center justify-between text-sm"><span>Subtotal</span><span className="font-bold">{brl(subtotal)}</span></div>
              <div className="flex items-center gap-2">
                <span className="text-sm">Desconto</span>
                <input type="number" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="cart-discount"
                       className="flex-1 h-10 rounded-lg border border-slate-200 px-3 text-right" placeholder="0,00" />
              </div>
              <div className="flex items-center justify-between text-lg"><span className="font-bold">Total</span><span className="font-black text-orange-600">{brl(total)}</span></div>
              <button disabled={totalItems === 0} onClick={() => { setShowCart(false); setPay(true); }} data-testid="cart-checkout"
                      className="w-full h-12 rounded-xl bg-orange-500 text-white font-black disabled:opacity-50 active:scale-[0.98]">Finalizar pagamento</button>
            </div>
          </div>
        </div>
      )}

      {/* Payment modal */}
      {pay && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center sm:justify-center" onClick={() => setPay(false)} data-testid="pay-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="font-black text-xl">Pagamento · {brl(total)}</div>
              <button onClick={() => setPay(false)}><X /></button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {METHODS.map((m) => (
                <button key={m.id} onClick={() => setMethod(m.id)} data-testid={`pay-method-${m.id}`}
                        className={`p-3 rounded-xl border flex flex-col items-center gap-1 active:scale-95 ${method === m.id ? 'border-orange-500 bg-orange-50 text-orange-600' : 'border-slate-200'}`}>
                  <m.icon size={18} />
                  <span className="text-[11px] font-bold">{m.label}</span>
                </button>
              ))}
            </div>
            {method === 'cash' && (
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-600">Valor recebido</label>
                <input type="number" step="0.01" value={amountReceived} onChange={(e) => setAmountReceived(e.target.value)} data-testid="pay-received"
                       className="w-full h-12 rounded-xl border border-slate-200 px-4 text-lg font-bold" placeholder={brl(total)} />
                <div className="grid grid-cols-4 gap-2">
                  {[5, 10, 20, 50, 100, 200, total].map((v) => (
                    <button key={`quick-${v}`} onClick={() => setAmountReceived(String(v))} className="h-9 rounded-lg bg-slate-100 text-sm font-bold active:scale-95">
                      {brl(v)}
                    </button>
                  ))}
                </div>
                {amountReceived && Number(amountReceived) >= total && (
                  <div className="text-sm">Troco: <span className="font-black text-green-600">{brl(Number(amountReceived) - total)}</span></div>
                )}
              </div>
            )}
            <button onClick={finalize} disabled={processing} data-testid="pay-confirm"
                    className="w-full h-14 rounded-2xl bg-green-500 text-white font-black flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50">
              <CheckCircle2 size={20} /> {processing ? 'Processando...' : 'Confirmar venda'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
